import type { FileDeletedReason } from "./file-unavailable";

type FolderTree = {
  uuid: string;
  name: string;
  group?: { name: string } | null;
  files: {
    uuid: string;
    name: string;
    dateDeleted?: string | null;
    deletedReason?: FileDeletedReason | null;
  }[];
  childFolders?: FolderTree[];
};
type FolderInfo = {
  uuid: string;
  name: string;
  path: string;
  files: FileInfo[];
};
export type FileInfo = {
  uuid: string;
  name: string;
  folderPath: string;
  folderUuid: string;
  groupName?: string | null;
  dateDeleted?: string | null;
  deletedReason?: FileDeletedReason | null;
};

/**
 * Kit knowledge "roots" for the sidebar / Knowledge index.
 *
 * Normally: folders with no parent.
 * Expert Link Connector Folder: linked roots are nested under Expert.folder_uuid
 * for RAG, so they have a parent that is NOT in the kit folder set — still show them.
 */
export function getKitKnowledgeRootFolders<
  T extends { uuid: string; parentFolderUuid: string | null },
>(folders: T[]): T[] {
  const kitFolderUuids = new Set(folders.map((f) => f.uuid));
  return folders.filter(
    (f) => !f.parentFolderUuid || !kitFolderUuids.has(f.parentFolderUuid),
  );
}

const flattenFolders = (
  folders: FolderTree[],
  parentPath: string,
): FolderInfo[] => {
  return folders.reduce((acc, folder) => {
    const path = parentPath ? `${parentPath}/${folder.name}` : folder.name;
    return [
      ...acc,
      {
        uuid: folder.uuid,
        name: folder.name,
        path: path,
        files: folder.files.map((file) => ({
          uuid: file.uuid,
          name: file.name,
          folderUuid: folder.uuid,
          folderPath: path,
          groupName: folder.group?.name ?? null,
          dateDeleted: file.dateDeleted ?? null,
          deletedReason: file.deletedReason ?? null,
        })),
      },
      ...flattenFolders(folder.childFolders ?? [], path),
    ];
  }, [] as FolderInfo[]);
};

export const getFilesFromFolders = (folders: FolderTree[]): FileInfo[] => {
  const allFolders = flattenFolders(folders, "");
  return allFolders.map((folder) => folder.files).flat();
};
